import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon } from '@mui/icons-material';
import axios from 'axios';

interface Category {
  id: string;
  name: string;
  code?: string;
  reservationPercentage?: number;
  isDefault: boolean;
  isActive: boolean;
}

const CategoryMaster: React.FC = () => {
  const [items, setItems] = useState<Category[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    reservationPercentage: '' as number | '' ,
    isDefault: false,
    isActive: true,
  });
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetchItems();
  }, [page, pageSize, search]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const skip = page * pageSize;
      const { data } = await axios.get('/system-settings/category', {
        params: { search, skip, take: pageSize },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = useMemo(() => items, [items]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      code: '',
      reservationPercentage: '',
      isDefault: false,
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (item: Category) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      code: item.code || '',
      reservationPercentage: typeof item.reservationPercentage === 'number' ? item.reservationPercentage : '',
      isDefault: !!item.isDefault,
      isActive: !!item.isActive,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload: any = {
        name: formData.name.trim(),
        code: formData.code.trim() || undefined,
        reservationPercentage:
          formData.reservationPercentage === '' ? undefined : Number(formData.reservationPercentage),
        isDefault: formData.isDefault,
        isActive: formData.isActive,
      };
      if (editingItem) {
        await axios.patch(`/system-settings/category/${editingItem.id}`, payload);
      } else {
        await axios.post('/system-settings/category', payload);
      }
      setDialogOpen(false);
      await fetchItems();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save category');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await axios.delete(`/system-settings/category/${id}`);
        await fetchItems();
      } catch (error: any) {
        alert(error.response?.data?.message || 'Cannot delete category. It may be assigned to students.');
      }
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/category/${id}/toggle-status`);
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isActive: !currentStatus } : item
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle status');
      fetchItems();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Category</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Create
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <TextField
          label="Search"
          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
          size="small"
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <InputLabel id="category-show-entries-label">Show entries</InputLabel>
          <Select
            labelId="category-show-entries-label"
            value={pageSize}
            size="small"
            onChange={(e) => {
              setPage(0);
              setPageSize(Number(e.target.value));
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Category Name</TableCell>
              <TableCell>Reservation %</TableCell>
              <TableCell>Default</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No categories found
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>
                    {typeof item.reservationPercentage === 'number'
                      ? `${item.reservationPercentage}%`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.isDefault ? 'Yes' : 'No'}
                      color={item.isDefault ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                  <Switch
                    checked={item.isActive}
                    onChange={() => handleToggleStatus(item.id, item.isActive)}
                    color="primary"
                    size="small"
                  />
                </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      title="View"
                      onClick={() =>
                        alert(
                          `Category: ${item.name}\nCode: ${item.code || '-'}\nReservation: ${
                            typeof item.reservationPercentage === 'number' ? `${item.reservationPercentage}%` : '-'
                          }\nDefault: ${item.isDefault ? 'Yes' : 'No'}\nStatus: ${item.isActive ? 'Active' : 'Inactive'}`
                        )
                      }
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton size="small" title="Edit" onClick={() => handleEdit(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" title="Delete" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Category' : 'Create Category'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
            />
            <TextField
              label="Reservation Percentage"
              type="number"
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              value={formData.reservationPercentage}
              onChange={(e) => {
                const val = e.target.value;
                setFormData({
                  ...formData,
                  reservationPercentage: val === '' ? '' : Number(val),
                });
              }}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
              }
              label="Set as Default"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryMaster;
